// Order is only defined within a unit family; band labels before they compete as a spine level.
import { isPlaceholderValue } from "./studioTypes";

const SIZE_TOKEN = /-?\d+(?:\.\d+)?/;

export interface SizeFamily {
  family: string;
  magnitude: number;
}

/** Bare `oz` is its own family so it never ranks against mass or fluid ounces. */
export const sizeFamilyOf = (label: string): SizeFamily => {
  const magnitude = parseFloat(label.match(SIZE_TOKEN)?.[0] ?? "0") || 0;
  if (/fl\s*oz/i.test(label)) return { family: "volume", magnitude };
  if (/\bml\b/i.test(label)) return { family: "volume", magnitude: magnitude * 0.033814 };
  if (/\bl\b/i.test(label)) return { family: "volume", magnitude: magnitude * 33.814 };
  if (/\blb\b|pound/i.test(label)) return { family: "mass", magnitude: magnitude * 16 };
  if (/\boz\b/i.test(label)) return { family: "oz", magnitude };
  if (/\bg\b/i.test(label)) return { family: "mass", magnitude: magnitude * 0.035274 };
  if (/\bct\b|count|\bpk\b|pack/i.test(label)) return { family: "count", magnitude };
  if (/load/i.test(label)) return { family: "loads", magnitude };
  return { family: magnitude > 0 ? "bare" : "other", magnitude };
};

export const UNKNOWN_SIZE_FAMILY = "other";

/** Unmeasured labels are excluded from order checks, never counted as violations. */
export const sizesComparable = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const left = sizeFamilyOf(String(a ?? ""));
  const right = sizeFamilyOf(String(b ?? ""));
  // Magnitude required: a tier name can carry a unit without being a size.
  if (left.magnitude <= 0 || right.magnitude <= 0) return false;
  if (left.family === UNKNOWN_SIZE_FAMILY || right.family === UNKNOWN_SIZE_FAMILY) return false;
  return left.family === right.family;
};

const FAMILY_UNIT: Readonly<Record<string, string>> = {
  volume: "fl oz",
  oz: "oz",
  mass: "lb",
  count: "ct",
  loads: "loads",
  bare: "pack",
};

const TIER_NAMES: Readonly<Record<number, readonly string[]>> = {
  1: ["One size"],
  2: ["Small", "Large"],
  3: ["Small", "Medium", "Large"],
};

const MAX_BANDS = 3;
const MIN_BAND_SKUS = 3;

/** Snap cuts to the largest nearby ratio gap — pack sizes step multiplicatively. */
const bandsForFamily = (
  entries: readonly { value: string; magnitude: number }[],
): Map<string, number> => {
  const weightOf = new Map<number, number>();
  const valuesOf = new Map<number, Set<string>>();
  for (const entry of entries) {
    weightOf.set(entry.magnitude, (weightOf.get(entry.magnitude) ?? 0) + 1);
    valuesOf.set(entry.magnitude, (valuesOf.get(entry.magnitude) ?? new Set()).add(entry.value));
  }
  const magnitudes = [...weightOf.keys()].sort((a, b) => a - b);
  const assign = (cuts: readonly number[]): Map<string, number> => {
    const out = new Map<string, number>();
    let band = 0;
    magnitudes.forEach((magnitude, index) => {
      if (index > 0 && cuts.includes(index)) band += 1;
      for (const value of valuesOf.get(magnitude) ?? []) out.set(value, band);
    });
    return out;
  };

  const bandCount = Math.max(
    1,
    Math.min(MAX_BANDS, magnitudes.length, Math.floor(entries.length / MIN_BAND_SKUS)),
  );
  if (bandCount < 2) return assign([]);

  const gapAt = (index: number) => magnitudes[index] / (magnitudes[index - 1] || 1);
  const total = entries.length;
  const cumulative: number[] = [];
  let running = 0;
  for (const magnitude of magnitudes) {
    running += weightOf.get(magnitude) ?? 0;
    cumulative.push(running);
  }
  const cuts: number[] = [];
  for (let band = 1; band < bandCount; band += 1) {
    const target = (total * band) / bandCount;
    let nearest = 1;
    for (let index = 1; index < magnitudes.length; index += 1) {
      if (Math.abs(cumulative[index - 1] - target) < Math.abs(cumulative[nearest - 1] - target)) {
        nearest = index;
      }
    }
    const window = Math.max(1, Math.round(magnitudes.length / (bandCount * 3)));
    let best = nearest;
    for (
      let index = Math.max(1, nearest - window);
      index <= Math.min(magnitudes.length - 1, nearest + window);
      index += 1
    ) {
      if (!cuts.includes(index) && gapAt(index) > gapAt(best)) best = index;
    }
    if (!cuts.includes(best)) cuts.push(best);
  }
  return assign(cuts.sort((a, b) => a - b));
};

export interface ValueBanding {
  bandOf: ReadonlyMap<string, string>;
  bands: readonly string[];
  coverage: number;
}

/** One entry per SKU so repeats carry weight. */
export const deriveValueBands = (values: readonly (string | null | undefined)[]): ValueBanding => {
  const byFamily = new Map<string, { value: string; magnitude: number }[]>();
  let measured = 0;
  for (const raw of values) {
    const label = String(raw ?? "");
    const parsed = sizeFamilyOf(label);
    if (parsed.family === UNKNOWN_SIZE_FAMILY) continue;
    measured += 1;
    byFamily.set(parsed.family, [
      ...(byFamily.get(parsed.family) ?? []),
      { value: label, magnitude: parsed.magnitude },
    ]);
  }
  const multiFamily = byFamily.size > 1;
  const bandOf = new Map<string, string>();
  const bands: string[] = [];
  const families = [...byFamily.entries()].sort(
    (a, b) =>
      Math.min(...a[1].map((entry) => entry.magnitude)) -
      Math.min(...b[1].map((entry) => entry.magnitude)),
  );
  for (const [family, entries] of families) {
    const assignment = bandsForFamily(entries);
    const bandCount = new Set(assignment.values()).size;
    const names = TIER_NAMES[Math.min(bandCount, MAX_BANDS)] ?? TIER_NAMES[MAX_BANDS];
    const unit = FAMILY_UNIT[family] ?? family;
    for (let band = 0; band < bandCount; band += 1) {
      const label = multiFamily ? `${names[band]} (${unit})` : names[band];
      bands.push(label);
      for (const [value, assigned] of assignment) {
        if (assigned === band) bandOf.set(value, label);
      }
    }
  }
  return { bandOf, bands, coverage: values.length ? measured / values.length : 0 };
};

/* ── Size-likeness ─────────────────────────────────────────────────────────
   Whether a set of values speaks size at all. */

/** Merchant size tiers, smallest first — how "Medium" sorts before "Large"
 *  when the label is a named band rather than a magnitude. */
const NAMED_SIZE_LADDER: readonly string[] = [
  "one size",
  "extra small",
  "x-small",
  "small",
  "medium",
  "large",
  "extra large",
  "x-large",
  "jumbo",
  "tub",
];

/** Share of a value set that must parse for the set to count as size-like. */
export const SIZE_LIKE_SHARE = 0.6;

const ladderRank = (value: string): number => NAMED_SIZE_LADDER.indexOf(value.trim().toLowerCase());

/** Does this value speak size — a magnitude in a unit family, or a named band? */
const isSizeLikeValue = (value: string): boolean =>
  sizeFamilyOf(value).magnitude > 0 || ladderRank(value) >= 0;

/** Share of the non-placeholder values that are size-like. */
export const sizeLikeShare = (values: readonly (string | null)[]): number => {
  const live = values
    .map((value) => String(value ?? ""))
    .filter((value) => !isPlaceholderValue(value));
  if (live.length === 0) return 0;
  return live.filter(isSizeLikeValue).length / live.length;
};
